import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { EntityFilterProvider } from './contexts/EntityFilterContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminProtectedRoute } from './components/AdminProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import ResponsivePreview from './components/admin/ResponsivePreview';

// Critical path pages (eager load) - needed for initial render
import BusinessSuiteLandingPage from './pages/BusinessSuiteLandingPage';
import MarketingHomePage from './pages/marketing/HomePage';
import SignupPage from './pages/SignupPage';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import { SSOCallbackPage } from './components/SSOLoginButton';

// Loading fallback component for lazy-loaded pages
const PageLoader = () => (
  <div className="min-h-screen bg-[#0a0f0d] flex items-center justify-center">
    <div className="text-[#2D7D6F] text-lg">Loading...</div>
  </div>
);

// Lazy load all other pages for better initial bundle size
const LandingPage = lazy(() => import('./pages/LandingPage'));
const GRCLandingPage = lazy(() => import('./pages/GRCLandingPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const IntelligentOnboardingPage = lazy(() => import('./pages/IntelligentOnboardingPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const OperationsDashboardPage = lazy(() => import('./pages/OperationsDashboardPage'));
const FrameworkDetailPage = lazy(() => import('./pages/FrameworkDetailPage'));
const RiskRegisterPage = lazy(() => import('./pages/RiskRegisterPage'));
const InformationAssetRegisterPage = lazy(() => import('./pages/InformationAssetRegisterPage'));
const ImpactAssessmentPage = lazy(() => import('./pages/ImpactAssessmentPage'));
const AssessmentDetailPage = lazy(() => import('./pages/AssessmentDetailPage'));
const CreateRiskPage = lazy(() => import('./pages/CreateRiskPage'));
const RiskDetailPage = lazy(() => import('./pages/RiskDetailPage'));
const RiskMatrixPage = lazy(() => import('./pages/RiskMatrixPage'));
const RiskObjectivesReportPage = lazy(() => import('./pages/RiskObjectivesReportPage'));
const BusinessObjectivesPage = lazy(() => import('./pages/BusinessObjectivesPage'));
const DocumentLibraryPage = lazy(() => import('./pages/DocumentLibraryPage'));
const QuestionnairesPage = lazy(() => import('./pages/QuestionnairesPage'));
const QuestionnaireDetailPage = lazy(() => import('./pages/QuestionnaireDetailPage'));
const TrustPortalPage = lazy(() => import('./pages/TrustPortalPage'));
const PublicTrustPage = lazy(() => import('./pages/PublicTrustPage'));
const PublicTrustPageV2 = lazy(() => import('./pages/PublicTrustPageV2'));
const PricingPage = lazy(() => import('./pages/PricingPageNew'));
const SubscriptionSuccessPage = lazy(() => import('./pages/SubscriptionSuccessPage'));
const SubscriptionManagePage = lazy(() => import('./pages/SubscriptionManagePage'));
const SubscriptionManagementPage = lazy(() => import('./pages/SubscriptionManagementPage'));
const ModuleUpgradePage = lazy(() => import('./pages/ModuleUpgradePage'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const APIKeyManagementPage = lazy(() => import('./pages/APIKeyManagementPage'));
const CustomFrameworksPage = lazy(() => import('./pages/CustomFrameworksPage'));
const AuditReportsPage = lazy(() => import('./pages/AuditReportsPage'));
const TeamManagementPage = lazy(() => import('./pages/TeamManagementPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const QMSDashboardPage = lazy(() => import('./pages/QMSDashboardPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PolicyBuilderPage = lazy(() => import('./pages/PolicyBuilderPage'));
const ComplianceCalendarPage = lazy(() => import('./pages/ComplianceCalendarPage'));
const AdvancedDashboardPage = lazy(() => import('./pages/AdvancedDashboardPage'));
const SubprocessorRegisterPage = lazy(() => import('./pages/SubprocessorRegisterPage'));
const SupplierJourneyPage = lazy(() => import('./pages/SupplierJourneyPage'));
const CertificationJourneyPage = lazy(() => import('./pages/CertificationJourneyPage'));
const IncidentJourneyPage = lazy(() => import('./pages/IncidentJourneyPage'));
const BoardJourneyPage = lazy(() => import('./pages/BoardJourneyPage'));
const VendorRenewalJourneyPage = lazy(() => import('./pages/VendorRenewalJourneyPage'));
const MATargetJourneyPage = lazy(() => import('./pages/MATargetJourneyPage'));
const DataProtectionPage = lazy(() => import('./pages/DataProtectionPage'));
const QMSPortalPage = lazy(() => import('./pages/QMSPortalPage'));
const DataProtectionPortalPage = lazy(() => import('./pages/DataProtectionPortalPage'));
const AuditorPortalPage = lazy(() => import('./pages/AuditorPortalPage'));
const AuditorManagementPage = lazy(() => import('./pages/AuditorManagementPage'));
const PrivacyToolsPage = lazy(() => import('./pages/PrivacyToolsPage'));
const PublicBreachCalculatorPage = lazy(() => import('./pages/PublicBreachCalculatorPage'));
const PublicDealCalculatorPage = lazy(() => import('./pages/PublicDealCalculatorPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PartnerPage = lazy(() => import('./pages/PartnerPage'));
const DueDiligencePage = lazy(() => import('./pages/DueDiligencePage'));
const CustomFrameworkBuilderPage = lazy(() => import('./pages/CustomFrameworkBuilderPage'));
const DDSupplierPortalPage = lazy(() => import('./pages/DDSupplierPortalPage'));
const InternalAuditPage = lazy(() => import('./pages/InternalAuditPage'));
const SSOSettingsPage = lazy(() => import('./pages/SSOSettingsPage'));
const MultiEntityPage = lazy(() => import('./pages/MultiEntityPage'));
const EmployeeTrainingPage = lazy(() => import('./pages/EmployeeTrainingPage'));
const TrainingExecutionPage = lazy(() => import('./pages/TrainingExecutionPage'));
const TrainingAnalyticsPage = lazy(() => import('./pages/TrainingAnalyticsPage'));
const TrainingHistoryPage = lazy(() => import('./pages/TrainingHistoryPage'));
const VideoCoursePlayerPage = lazy(() => import('./pages/VideoCoursePlayerPage'));
const BoardPackPage = lazy(() => import('./pages/BoardPackPage'));
const BoardManagementPage = lazy(() => import('./pages/BoardManagementPage'));
const SharedBoardPackPage = lazy(() => import('./pages/SharedBoardPackPage'));
const PublicSitePage = lazy(() => import('./pages/PublicSitePage'));
const PublicBreachCalculatorV2Page = lazy(() => import('./pages/PublicBreachCalculatorV2Page'));
const AdminSupportPage = lazy(() => import('./pages/AdminSupportPage'));
const IncidentManagementPage = lazy(() => import('./pages/IncidentManagementPage'));
const AdminLeadsPage = lazy(() => import('./pages/AdminLeadsPage'));
const LeadCRMPage = lazy(() => import('./pages/LeadCRMPage'));
const AdminNotificationsPage = lazy(() => import('./pages/AdminNotificationsPage'));
const TrustAppMobile = lazy(() => import('./pages/TrustAppMobile'));
const ExecAppMobile = lazy(() => import('./pages/ExecAppMobile'));
const UnifiedOnboardingPage = lazy(() => import('./pages/UnifiedOnboardingPage'));
const UnifiedSignupPage = lazy(() => import('./pages/UnifiedSignupPage'));
const SignupModuleSelectPage = lazy(() => import('./pages/SignupModuleSelectPage'));
const FoundingMemberDemo = lazy(() => import('./pages/FoundingMemberDemo'));
const GRCOnboardingPage = lazy(() => import('./pages/GRCOnboardingPage'));
const FrameworkManagementPage = lazy(() => import('./pages/FrameworkManagementPage'));
const RiskOnboardingPage = lazy(() => import('./pages/RiskOnboardingPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const APIDocumentationPage = lazy(() => import('./pages/APIDocumentationPage'));
import EnterpriseQuestionnairePage from './pages/EnterpriseQuestionnairePage';
import PlatformWalkthroughPage from './pages/PlatformWalkthroughPage';

// Lazy load AI Governance pages (heavy, rarely accessed initially)
const AIGovernanceDashboardPage = lazy(() => import('./pages/AIGovernanceDashboardPage'));
const AISystemsPage = lazy(() => import('./pages/AISystemsPage'));
const AIRisksPage = lazy(() => import('./pages/AIRisksPage'));
const AIIncidentsPage = lazy(() => import('./pages/AIIncidentsPage'));
const AITrainingPage = lazy(() => import('./pages/AITrainingPage'));
const AITrainingCoursePage = lazy(() => import('./pages/AITrainingCoursePage'));
const AIPoliciesPage = lazy(() => import('./pages/AIPoliciesPage'));
const AIVendorsPage = lazy(() => import('./pages/AIVendorsPage'));
const AIOversightPage = lazy(() => import('./pages/AIOversightPage'));
const AIModelCardsPage = lazy(() => import('./pages/AIModelCardsPage'));
const AIBiasAuditsPage = lazy(() => import('./pages/AIBiasAuditsPage'));
const TCAnalyzerPage = lazy(() => import('./pages/TCAnalyzerPage'));
const StandardsAcademyPage = lazy(() => import('./pages/StandardsAcademyPage'));
const AIStrategyPage = lazy(() => import('./pages/AIStrategyPage'));
const COIRegisterPage = lazy(() => import('./pages/COIRegisterPage'));
const COIDeclarePage = lazy(() => import('./pages/COIDeclarePage'));
const AIStakeholdersPage = lazy(() => import('./pages/AIStakeholdersPage'));
const OutreachPage = lazy(() => import('./pages/OutreachPage'));

const DataExportPage = lazy(() => import('./pages/DataExportPage'));
const ComplianceQueryEngine = lazy(() => import('./pages/ComplianceQueryEngine'));

// Lazy load Product pages (SEO landing pages, not needed on initial load)
const ProductPage = lazy(() => import('./pages/ProductPage'));
const ISO27001ProductPage = lazy(() => import('./pages/ISO27001ProductPage'));
const SOC2ProductPage = lazy(() => import('./pages/SOC2ProductPage'));
const GDPRProductPage = lazy(() => import('./pages/GDPRProductPage'));
const CyberEssentialsProductPage = lazy(() => import('./pages/CyberEssentialsProductPage'));
const HIPAAProductPage = lazy(() => import('./pages/HIPAAProductPage'));
const PCIDSSProductPage = lazy(() => import('./pages/PCIDSSProductPage'));
const NISTProductPage = lazy(() => import('./pages/NISTProductPage'));
const EUAIActProductPage = lazy(() => import('./pages/EUAIActProductPage'));
const GeographyPage = lazy(() => import('./pages/GeographyPage'));

// Lazy load module dashboards (Pro-tier, loaded on demand)
const PMODashboardPage = lazy(() => import('./pages/PMODashboardPage'));
const PMOProjectDetailPage = lazy(() => import('./pages/PMOProjectDetailPage'));
const PMOAnalyticsPage = lazy(() => import('./pages/PMOAnalyticsPage'));
const ContractsDashboardPage = lazy(() => import('./pages/ContractsDashboardPage'));
const MADashboardPage = lazy(() => import('./pages/MADashboardPage'));
const CoSecDashboardPage = lazy(() => import('./pages/CoSecDashboardPage'));
const CoSecOnboardingPage = lazy(() => import('./pages/CoSecOnboardingPage'));
const CoSecTasksPage = lazy(() => import('./pages/CoSecTasksPage'));
const CoSecMeetingsPage = lazy(() => import('./pages/CoSecMeetingsPage'));
const CoSecCalendarPage = lazy(() => import('./pages/CoSecCalendarPage'));
const EntitiesListPage = lazy(() => import('./pages/EntitiesListPage'));
const EntityGovernancePage = lazy(() => import('./pages/EntityGovernancePage'));
const CorporateStructurePage = lazy(() => import('./pages/CorporateStructurePage'));
const FilingCalendarPage = lazy(() => import('./pages/FilingCalendarPage'));
const OfficerDirectoryPage = lazy(() => import('./pages/OfficerDirectoryPage'));
const AuthorityEnginePage = lazy(() => import('./pages/AuthorityEnginePage'));

const CoSecLandingPage = lazy(() => import('./pages/CoSecLandingPage'));

// Lazy load M&A detail pages (heavy)
const VendorDetailPage = lazy(() => import('./pages/VendorDetailPage'));
const DealDetailPage = lazy(() => import('./pages/DealDetailPage'));
const DealRoomPage = lazy(() => import('./pages/DealRoomPage'));
const MADueDiligencePage = lazy(() => import('./pages/MADueDiligencePage'));
const IntegrationPlaybooksPage = lazy(() => import('./pages/IntegrationPlaybooksPage'));
const MAPlusComparisonPage = lazy(() => import('./pages/MAPlusComparisonPage'));
const MAPlusEmploymentAnalyserPage = lazy(() => import('./pages/MAPlusEmploymentAnalyserPage'));
const MAPlusContractAnalyserPage = lazy(() => import('./pages/MAPlusContractAnalyserPage'));
const MAComparisonReportPage = lazy(() => import('./pages/MAComparisonReportPage'));
const MAAnalyticsDashboard = lazy(() => import('./pages/MAAnalyticsDashboard'));
const ContractDetailPage = lazy(() => import('./pages/ContractDetailPage'));

const MAPlusLandingPage = lazy(() => import('./pages/MAPlusLandingPage'));
const MAPlusToolLandingPage = lazy(() => import('./pages/MAPlusToolLandingPage'));
const StandaloneEmploymentAnalyserPage = lazy(() => import('./pages/StandaloneEmploymentAnalyserPage'));
const StandaloneContractAnalyserPage = lazy(() => import('./pages/StandaloneContractAnalyserPage'));
const ExternalAccessPortal = lazy(() => import('./pages/ExternalAccessPortal'));
const ExternalComparisonPortal = lazy(() => import('./pages/ExternalComparisonPortal'));
const ContractsLandingPage = lazy(() => import('./pages/ContractsLandingPage'));
const CDEMappingPage = lazy(() => import('./pages/CDEMappingPage'));
const VulnerabilityScannerPage = lazy(() => import('./pages/VulnerabilityScannerPage'));

// Tender/Procurement pages
const TendersDashboardPage = lazy(() => import('./pages/TendersDashboardPage'));
const PSLPage = lazy(() => import('./pages/PSLPage'));
const TenderDetailPage = lazy(() => import('./pages/TenderDetailPage'));
const VendorPortalPage = lazy(() => import('./pages/VendorPortalPage'));
const WebhookSettingsPage = lazy(() => import('./pages/WebhookSettingsPage'));
const IntegrationsSettingsPage = lazy(() => import('./pages/IntegrationsSettingsPage'));
const ProcurementAnalyticsPage = lazy(() => import('./pages/ProcurementAnalyticsPage'));
const VendorScorecardPage = lazy(() => import('./pages/VendorScorecardPage'));
const ContractRenewalsPage = lazy(() => import('./pages/ContractRenewalsPage'));
const SavingsTrackerPage = lazy(() => import('./pages/SavingsTrackerPage'));

// Lazy load solution pages for better Core Web Vitals
const GRCSolutionPage = lazy(() => import('./pages/GRCSolutionPage'));
const MarketingGRCPage = lazy(() => import('./pages/marketing/GRCPage'));
const MarketingMAPage = lazy(() => import('./pages/marketing/MAPage'));
const MarketingSuppliersPage = lazy(() => import('./pages/marketing/SuppliersPage'));
const CompanySecretarySolutionPage = lazy(() => import('./pages/CompanySecretarySolutionPage'));
const PMOSolutionPage = lazy(() => import('./pages/PMOSolutionPage'));
const ContractsSolutionPage = lazy(() => import('./pages/ContractsSolutionPage'));
const MarketingContractsPage = lazy(() => import('./pages/marketing/ContractsPage'));
const MarketingPMOPage = lazy(() => import('./pages/marketing/PMOPage'));
const MarketingCompanySecretaryPage = lazy(() => import('./pages/marketing/CompanySecretaryPage'));
const MarketingMAPlusPage = lazy(() => import('./pages/marketing/MAPlusPage'));
const MarketingPricingPage = lazy(() => import('./pages/marketing/PricingPage'));
const MarketingAboutPage = lazy(() => import('./pages/marketing/AboutPage'));
const MarketingComparisonPage = lazy(() => import('./pages/marketing/ComparisonPage'));
const MASolutionPage = lazy(() => import('./pages/MASolutionPage'));
const MALandingPage = lazy(() => import('./pages/MALandingPage'));
const MAAcquirerPage = lazy(() => import('./pages/MAAcquirerPage'));
const MATargetPage = lazy(() => import('./pages/MATargetPage'));

// Blog pages
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogCategoryPage = lazy(() => import('./pages/BlogCategoryPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));

// Press & Tools pages
const PressKitPage = lazy(() => import('./pages/PressKitPage'));
const ComplianceReadinessCalculator = lazy(() => import('./pages/tools/ComplianceReadinessCalculator'));

const TargetDetailPage = lazy(() => import('./pages/TargetDetailPage'));
const ModularPricingPage = lazy(() => import('./pages/ModularPricingPage'));
const FoundingMemberBenefitsPage = lazy(() => import('./pages/FoundingMemberBenefitsPage'));
const CommandCenterPage = lazy(() => import('./pages/CommandCenterPage'));
const RFPAssistantPage = lazy(() => import('./pages/RFPAssistantPage'));
const QuickCapturePage = lazy(() => import('./pages/QuickCapturePage'));
const NotificationHubPage = lazy(() => import('./pages/NotificationHubPage'));
const InvestmentReadinessPage = lazy(() => import('./pages/InvestmentReadinessPage'));
const MAOnboardingPage = lazy(() => import('./pages/MAOnboardingPage'));
const MAMarketplacePage = lazy(() => import('./pages/MAMarketplacePage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const AutomationWorkflowsPage = lazy(() => import('./pages/AutomationWorkflowsPage'));
const RelationshipGraphPage = lazy(() => import('./pages/RelationshipGraphPage'));
const VendorLifecyclePage = lazy(() => import('./pages/VendorLifecyclePage'));
const DocumentVaultPage = lazy(() => import('./pages/DocumentVaultPage'));
const GlobalSearchPage = lazy(() => import('./pages/GlobalSearchPage'));
const ActivityFeedPage = lazy(() => import('./pages/ActivityFeedPage'));
const UnifiedCalendarPage = lazy(() => import('./pages/UnifiedCalendarPage'));
const AIInsightsPage = lazy(() => import('./pages/AIInsightsPage'));
const FrameworkBuilderPage = lazy(() => import('./pages/FrameworkBuilderPage'));
const UserSupportDeskPage = lazy(() => import('./pages/UserSupportDeskPage'));
const BoardPackEnhancedPage = lazy(() => import('./pages/BoardPackEnhancedPage'));
const AdvancedAnalyticsPage = lazy(() => import('./pages/AdvancedAnalyticsPage'));
const RegulatoryIntelligencePage = lazy(() => import('./pages/RegulatoryIntelligencePage'));
const RiskHeatmapDashboard = lazy(() => import('./pages/RiskHeatmapDashboard'));
const IncidentWarRoom = lazy(() => import('./pages/IncidentWarRoom'));
const RegulatoryImpactPage = lazy(() => import('./pages/RegulatoryImpactPage'));
const AuditPackGeneratorPage = lazy(() => import('./pages/AuditPackGeneratorPage'));
const ComplianceScorePage = lazy(() => import('./pages/ComplianceScorePage'));
const UpgradePathPage = lazy(() => import('./pages/UpgradePathPage'));
const BusinessContinuityPage = lazy(() => import('./pages/BusinessContinuityPage'));
const ESGPage = lazy(() => import('./pages/ESGPage'));
const ComplianceCalendar = lazy(() => import('./pages/ComplianceCalendar'));
const WorkflowManagement = lazy(() => import('./pages/WorkflowManagement'));
const DocumentAIRouter = lazy(() => import('./pages/DocumentAIRouter'));

// Sales pages (can be eagerly loaded since they're marketing)
import { GRCSalesPage, ContractsSalesPage, PMOSalesPage, MASalesPage, BoardSalesPage } from './pages/ModuleSalesPageNew';

import SupportBot from './components/SupportBot';
import DashboardLayout from './components/DashboardLayout';
import { ModuleGatedPage } from './components/ModuleGatedPage';
import CookieConsent from './components/CookieConsent';
import { PWAInstallPrompt, OfflineIndicator, UpdatePrompt } from './components/ui/pwa-components';
import './App.css';

// Protected Route with Sidebar Layout
const ProtectedRouteWithLayout = ({ children }) => (
  <ProtectedRoute>
    <DashboardLayout>
      {children}
    </DashboardLayout>
  </ProtectedRoute>
);

// Protected Route with Module Gating (for Pro-tier features)
const GatedRouteWithLayout = ({ module, requiredTier = 'Pro', children }) => (
  <ProtectedRoute>
    <DashboardLayout>
      <ModuleGatedPage module={module} requiredTier={requiredTier}>
        {children}
      </ModuleGatedPage>
    </DashboardLayout>
  </ProtectedRoute>
);

function AppRouter() {
  const location = useLocation();
  
  // Track page views on route changes for Google Analytics
  useEffect(() => {
    // Send page view to GA4 on every route change
    if (window.gtag) {
      window.gtag('config', 'G-P89HVDD8C5', {
        page_path: location.pathname + location.search,
        page_title: document.title
      });
    }
  }, [location.pathname, location.search]);
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<MarketingHomePage />} />
      <Route path="/legacy-home" element={<BusinessSuiteLandingPage />} />
      <Route path="/legacy-solutions/grc" element={<Suspense fallback={<PageLoader />}><GRCSolutionPage /></Suspense>} />
      <Route path="/legacy-solutions/ma" element={<Suspense fallback={<PageLoader />}><MALandingPage /></Suspense>} />
      
      {/* Solution Pages (SEO-optimized URLs) - Lazy loaded for better performance */}
      <Route path="/solutions/grc" element={<Suspense fallback={<PageLoader />}><MarketingGRCPage /></Suspense>} />
      <Route path="/solutions/suppliers" element={<Suspense fallback={<PageLoader />}><MarketingSuppliersPage /></Suspense>} />
      <Route path="/solutions/pmo" element={<Suspense fallback={<PageLoader />}><MarketingPMOPage /></Suspense>} />
      <Route path="/legacy-solutions/pmo" element={<Suspense fallback={<PageLoader />}><PMOSolutionPage /></Suspense>} />
      <Route path="/solutions/contracts" element={<Suspense fallback={<PageLoader />}><MarketingContractsPage /></Suspense>} />
      <Route path="/legacy-solutions/contracts" element={<Suspense fallback={<PageLoader />}><ContractsSolutionPage /></Suspense>} />
      <Route path="/solutions/ma" element={<Suspense fallback={<PageLoader />}><MarketingMAPage /></Suspense>} />
      <Route path="/solutions/ma/acquirer" element={<Suspense fallback={<PageLoader />}><MAAcquirerPage /></Suspense>} />
      <Route path="/solutions/ma/target" element={<Suspense fallback={<PageLoader />}><MATargetPage /></Suspense>} />
      <Route path="/solutions/ma/legacy" element={<Suspense fallback={<PageLoader />}><MASolutionPage /></Suspense>} />
      <Route path="/solutions/company-secretary" element={<Suspense fallback={<PageLoader />}><MarketingCompanySecretaryPage /></Suspense>} />
      <Route path="/legacy-solutions/company-secretary" element={<Suspense fallback={<PageLoader />}><CompanySecretarySolutionPage /></Suspense>} />
      
      {/* Blog Routes */}
      <Route path="/blog" element={<Suspense fallback={<PageLoader />}><BlogPage /></Suspense>} />
      <Route path="/blog/:category" element={<Suspense fallback={<PageLoader />}><BlogCategoryPage /></Suspense>} />
      <Route path="/blog/:category/:slug" element={<Suspense fallback={<PageLoader />}><BlogPostPage /></Suspense>} />
      
      {/* Legacy module routes (maintain backwards compatibility) */}
      <Route path="/grc" element={<Navigate to="/solutions/grc" replace />} />
      <Route path="/contracts-module" element={<ContractsLandingPage />} />
      <Route path="/pmo-software" element={<PMOSalesPage />} />
      <Route path="/ma-module" element={<Navigate to="/solutions/ma" replace />} />
      <Route path="/legacy-ma-module" element={<MASalesPage />} />
      <Route path="/ma-plus" element={<Suspense fallback={<PageLoader />}><MarketingMAPlusPage /></Suspense>} />
      <Route path="/about" element={<Suspense fallback={<PageLoader />}><MarketingAboutPage /></Suspense>} />
      <Route path="/vs/:rival" element={<Suspense fallback={<PageLoader />}><MarketingComparisonPage /></Suspense>} />
      <Route path="/ma-pro-plus" element={<Navigate to="/ma-plus" replace />} />
      <Route path="/legacy-ma-plus" element={<Suspense fallback={<PageLoader />}><MAPlusLandingPage /></Suspense>} />
      <Route path="/board" element={<BoardSalesPage />} />
      <Route path="/company-secretary" element={<Suspense fallback={<PageLoader />}><CoSecLandingPage /></Suspense>} />
      <Route path="/cosec-module" element={<Suspense fallback={<PageLoader />}><CoSecLandingPage /></Suspense>} />
      <Route path="/pricing" element={<Suspense fallback={<PageLoader />}><MarketingPricingPage /></Suspense>} />
      <Route path="/legacy-pricing" element={<Suspense fallback={<PageLoader />}><ModularPricingPage /></Suspense>} />
      <Route path="/founding-member" element={<Suspense fallback={<PageLoader />}><FoundingMemberBenefitsPage /></Suspense>} />
      <Route path="/pricing-legacy" element={<Suspense fallback={<PageLoader />}><PricingPage /></Suspense>} />
      {/* Module selection page - users choose their product first */}
      <Route path="/signup" element={<Suspense fallback={<PageLoader />}><SignupModuleSelectPage /></Suspense>} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/sso/callback" element={<SSOCallbackPage />} />
      
      {/* Unified Product Signup Pages (4-step flow) */}
      <Route path="/signup/grc" element={<Suspense fallback={<PageLoader />}><UnifiedSignupPage product="grc" /></Suspense>} />
      <Route path="/signup/company-secretary" element={<Suspense fallback={<PageLoader />}><UnifiedSignupPage product="cosec" /></Suspense>} />
      <Route path="/signup/cosec" element={<Suspense fallback={<PageLoader />}><UnifiedSignupPage product="cosec" /></Suspense>} />
      <Route path="/signup/pmo" element={<Suspense fallback={<PageLoader />}><UnifiedSignupPage product="pmo" /></Suspense>} />
      <Route path="/signup/contracts" element={<Suspense fallback={<PageLoader />}><UnifiedSignupPage product="contracts" /></Suspense>} />
      <Route path="/signup/ma" element={<Suspense fallback={<PageLoader />}><UnifiedSignupPage product="ma" /></Suspense>} />
      <Route path="/signup/full-platform" element={<Suspense fallback={<PageLoader />}><UnifiedSignupPage product="full_platform" /></Suspense>} />
      <Route path="/signup/success" element={<Suspense fallback={<PageLoader />}><SubscriptionSuccessPage /></Suspense>} />
      <Route path="/founding-member-demo" element={<Suspense fallback={<PageLoader />}><FoundingMemberDemo /></Suspense>} />
      
      {/* Fallback routes for signup flow - redirect to main signup */}
      <Route path="/signup/packages" element={<Navigate to="/signup" replace />} />
      <Route path="/signup/billing" element={<Navigate to="/signup" replace />} />
      <Route path="/signup/payment" element={<Navigate to="/signup" replace />} />
      
      {/* Product/Framework Pages - SEO optimized URLs (Lazy loaded) */}
      <Route path="/iso-27001-compliance-software" element={<Suspense fallback={<PageLoader />}><ISO27001ProductPage /></Suspense>} />
      <Route path="/iso27001" element={<Suspense fallback={<PageLoader />}><ISO27001ProductPage /></Suspense>} />
      <Route path="/soc2-compliance-software" element={<Suspense fallback={<PageLoader />}><SOC2ProductPage /></Suspense>} />
      <Route path="/soc2" element={<Suspense fallback={<PageLoader />}><SOC2ProductPage /></Suspense>} />
      <Route path="/gdpr-compliance-software" element={<Suspense fallback={<PageLoader />}><GDPRProductPage /></Suspense>} />
      <Route path="/gdpr" element={<Suspense fallback={<PageLoader />}><GDPRProductPage /></Suspense>} />
      <Route path="/cyber-essentials-compliance-software" element={<Suspense fallback={<PageLoader />}><CyberEssentialsProductPage /></Suspense>} />
      <Route path="/cyber-essentials" element={<Suspense fallback={<PageLoader />}><CyberEssentialsProductPage /></Suspense>} />
      <Route path="/hipaa-compliance-software" element={<Suspense fallback={<PageLoader />}><HIPAAProductPage /></Suspense>} />
      <Route path="/hipaa" element={<Suspense fallback={<PageLoader />}><HIPAAProductPage /></Suspense>} />
      <Route path="/pci-dss-compliance-software" element={<Suspense fallback={<PageLoader />}><PCIDSSProductPage /></Suspense>} />
      <Route path="/pci-dss" element={<Suspense fallback={<PageLoader />}><PCIDSSProductPage /></Suspense>} />
      <Route path="/nist-compliance-software" element={<Suspense fallback={<PageLoader />}><NISTProductPage /></Suspense>} />
      <Route path="/nist" element={<Suspense fallback={<PageLoader />}><NISTProductPage /></Suspense>} />
      <Route path="/eu-ai-act-compliance-software" element={<Suspense fallback={<PageLoader />}><EUAIActProductPage /></Suspense>} />
      
      {/* Geography Pages - Flat SEO-friendly URLs (Lazy loaded) */}
      <Route path="/uk-compliance-software" element={<Navigate to="/geo/uk" replace />} />
      <Route path="/europe-compliance-software" element={<Navigate to="/geo/europe" replace />} />
      <Route path="/us-compliance-software" element={<Navigate to="/geo/us" replace />} />
      <Route path="/uae-compliance-software" element={<Navigate to="/geo/uae" replace />} />
      <Route path="/global-compliance-software" element={<Navigate to="/geo/global" replace />} />
      {/* Legacy geo routes for backwards compatibility */}
      <Route path="/geo/uk" element={<Suspense fallback={<PageLoader />}><GeographyPage /></Suspense>} />
      <Route path="/geo/europe" element={<Suspense fallback={<PageLoader />}><GeographyPage /></Suspense>} />
      <Route path="/geo/us" element={<Suspense fallback={<PageLoader />}><GeographyPage /></Suspense>} />
      <Route path="/geo/uae" element={<Suspense fallback={<PageLoader />}><GeographyPage /></Suspense>} />
      <Route path="/geo/global" element={<Suspense fallback={<PageLoader />}><GeographyPage /></Suspense>} />
      
      <Route path="/trust/:userId" element={<Suspense fallback={<PageLoader />}><PublicTrustPageV2 /></Suspense>} />
      <Route path="/qms-portal/:userId" element={<Suspense fallback={<PageLoader />}><QMSPortalPage /></Suspense>} />
      <Route path="/dp-portal/:userId/:portalType?" element={<Suspense fallback={<PageLoader />}><DataProtectionPortalPage /></Suspense>} />
      <Route path="/auditor-portal/:accessToken" element={<Suspense fallback={<PageLoader />}><AuditorPortalPage /></Suspense>} />
      <Route path="/vendor-portal/:tenderId" element={<Suspense fallback={<PageLoader />}><VendorPortalPage /></Suspense>} />
      <Route path="/trust/v1/:userId" element={<Suspense fallback={<PageLoader />}><PublicTrustPage /></Suspense>} />
      <Route path="/subscription/success" element={<Suspense fallback={<PageLoader />}><SubscriptionSuccessPage /></Suspense>} />
      <Route path="/subscription/cancel" element={<Suspense fallback={<PageLoader />}><PricingPage /></Suspense>} />
      {/* Redirect old manage route to new settings page */}
      <Route path="/subscription/manage" element={<Navigate to="/subscription/settings" replace />} />
      <Route path="/subscription/upgrade" element={
        <ProtectedRouteWithLayout showSidebar={false}>
          <Suspense fallback={<PageLoader />}><ModuleUpgradePage /></Suspense>
        </ProtectedRouteWithLayout>
      } />
      <Route path="/subscription/settings" element={
        <ProtectedRouteWithLayout showSidebar={false}>
          <Suspense fallback={<PageLoader />}><SubscriptionManagementPage /></Suspense>
        </ProtectedRouteWithLayout>
      } />
      
      {/* Legal Pages */}
      <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPolicyPage /></Suspense>} />
      <Route path="/terms" element={<Suspense fallback={<PageLoader />}><TermsPage /></Suspense>} />
      <Route path="/contact" element={<Suspense fallback={<PageLoader />}><ContactPage /></Suspense>} />
      
      {/* Developer / API Documentation */}
      <Route path="/developers" element={<Suspense fallback={<PageLoader />}><APIDocumentationPage /></Suspense>} />
      <Route path="/api-docs" element={<Suspense fallback={<PageLoader />}><APIDocumentationPage /></Suspense>} />
      
      {/* Partner / Trade Sales */}
      <Route path="/partner" element={<Suspense fallback={<PageLoader />}><PartnerPage /></Suspense>} />
      
      {/* Press & Media */}
      <Route path="/press" element={<Suspense fallback={<PageLoader />}><PressKitPage /></Suspense>} />
      
      {/* Free Tools - Linkable Assets */}
      <Route path="/tools/compliance-calculator" element={<Suspense fallback={<PageLoader />}><ComplianceReadinessCalculator /></Suspense>} />
      <Route path="/breach-calculator" element={<Navigate to="/free-tools/breach-calculator" replace />} />
      <Route path="/free-tools/breach-calculator" element={<Suspense fallback={<PageLoader />}><PublicBreachCalculatorPage /></Suspense>} />
      <Route path="/free-tools/deal-calculator" element={<Suspense fallback={<PageLoader />}><PublicDealCalculatorPage /></Suspense>} />
      
      {/* Public Site - Redirect to main landing page */}
      <Route path="/public" element={<Navigate to="/" replace />} />
      <Route path="/public/breach-calculator" element={<Suspense fallback={<PageLoader />}><PublicBreachCalculatorV2Page /></Suspense>} />
      
      {/* Shared Board Pack (external access) */}
      <Route path="/board-pack/shared/:accessToken" element={<Suspense fallback={<PageLoader />}><SharedBoardPackPage /></Suspense>} />
      
      {/* Micro Pages (URL-only access) */}
      <Route path="/questionnaire/enterprise" element={<EnterpriseQuestionnairePage />} />
      <Route path="/walkthrough" element={<PlatformWalkthroughPage />} />
      
      {/* Convenience Redirects */}
      <Route path="/board-packs" element={<Navigate to="/pmo/board-packs" replace />} />
      
      {/* Executive Dashboard & Tools */}
      <Route path="/executive/risk-heatmap" element={
        <ProtectedRouteWithLayout>
          <Suspense fallback={<PageLoader />}><RiskHeatmapDashboard /></Suspense>
        </ProtectedRouteWithLayout>
      } />
      <Route path="/executive/compliance-calendar" element={
        <ProtectedRouteWithLayout>
          <Suspense fallback={<PageLoader />}><ComplianceCalendar /></Suspense>
        </ProtectedRouteWithLayout>
      } />
      <Route path="/executive/workflow-management" element={
        <ProtectedRouteWithLayout>
          <Suspense fallback={<PageLoader />}><WorkflowManagement /></Suspense>
        </ProtectedRouteWithLayout>
      } />
      <Route path="/executive/document-ai-router" element={
        <ProtectedRouteWithLayout>
          <Suspense fallback={<PageLoader />}><DocumentAIRouter /></Suspense>
        </ProtectedRouteWithLayout>
      } />
      <Route path="/warroom/:warRoomId" element={
        <ProtectedRouteWithLayout>
          <Suspense fallback={<PageLoader />}><IncidentWarRoom /></Suspense>
        </ProtectedRouteWithLayout>
      } />
      <Route path="/incident/:incidentId/warroom" element={
        <ProtectedRouteWithLayout>
          <Suspense fallback={<PageLoader />}><IncidentWarRoom /></Suspense>
        </ProtectedRouteWithLayout>
      } />
      <Route path="/regulatory-impact" element={
        <ProtectedRouteWithLayout>
          <Suspense fallback={<PageLoader />}><RegulatoryImpactPage /></Suspense>
        </ProtectedRouteWithLayout>
      } />
      <Route path="/audit-pack" element={
        <ProtectedRouteWithLayout>
          <Suspense fallback={<PageLoader />}><AuditPackGeneratorPage /></Suspense>
        </ProtectedRouteWithLayout>
      } />
      <Route path="/compliance-score" element={
        <ProtectedRouteWithLayout>
          <Suspense fallback={<PageLoader />}><ComplianceScorePage /></Suspense>
        </ProtectedRouteWithLayout>
      } />
      <Route path="/upgrade-path" element={
        <ProtectedRouteWithLayout>
          <Suspense fallback={<PageLoader />}><UpgradePathPage /></Suspense>
        </ProtectedRouteWithLayout>
      } />
      <Route path="/business-continuity" element={
        <ProtectedRouteWithLayout>
          <Suspense fallback={<PageLoader />}><BusinessContinuityPage /></Suspense>
        </ProtectedRouteWithLayout>
      } />
      <Route path="/esg" element={
        <ProtectedRouteWithLayout>
          <Suspense fallback={<PageLoader />}><ESGPage /></Suspense>
        </ProtectedRouteWithLayout>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
      <Route path="/admin/login" element={<Suspense fallback={<PageLoader />}><AdminLoginPage /></Suspense>} />
      <Route path="/admin/dashboard" element={
        <AdminProtectedRoute>
          <Suspense fallback={<PageLoader />}><AdminDashboardPage /></Suspense>
        </AdminProtectedRoute>
      } />
      <Route path="/admin/support" element={
        <AdminProtectedRoute>
          <Suspense fallback={<PageLoader />}><AdminSupportPage /></Suspense>
        </AdminProtectedRoute>
      } />
      <Route path="/admin/leads" element={
        <AdminProtectedRoute>
          <Suspense fallback={<PageLoader />}><AdminLeadsPage /></Suspense>
        </AdminProtectedRoute>
      } />
      <Route path="/admin/crm" element={
        <AdminProtectedRoute>
          <Suspense fallback={<PageLoader />}><LeadCRMPage /></Suspense>
        </AdminProtectedRoute>
      } />
      <Route path="/admin/notifications" element={
        <AdminProtectedRoute>
          <AdminNotificationsPage />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/api-keys" element={
        <AdminProtectedRoute>
          <Suspense fallback={<PageLoader />}><APIKeyManagementPage /></Suspense>
        </AdminProtectedRoute>
      } />
      
      <Route 
        path="/onboarding" 
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/smart-onboarding" 
        element={
          <ProtectedRoute>
            <IntelligentOnboardingPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/get-started" 
        element={
          <ProtectedRoute>
            <UnifiedOnboardingPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRouteWithLayout>
            <OperationsDashboardPage />
          </ProtectedRouteWithLayout>
        } 
      />
      {/* Redirect /dashboard/onboarding to main dashboard - onboarding is in signup flow */}
      <Route path="/dashboard/onboarding" element={<Navigate to="/dashboard" replace />} />
      <Route 
        path="/grc-dashboard" 
        element={
          <ProtectedRouteWithLayout>
            <DashboardPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/grc/onboarding" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><GRCOnboardingPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/risk/onboarding" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><RiskOnboardingPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/frameworks/manage" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><FrameworkManagementPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/framework/:frameworkId" 
        element={
          <ProtectedRouteWithLayout>
            <FrameworkDetailPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/risk-register" 
        element={
          <ProtectedRouteWithLayout>
            <RiskRegisterPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/risk-register/new" 
        element={
          <ProtectedRouteWithLayout>
            <CreateRiskPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/risk-register/:id" 
        element={
          <ProtectedRouteWithLayout>
            <RiskDetailPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/risk-register/matrix" 
        element={
          <ProtectedRouteWithLayout>
            <RiskMatrixPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/information-assets" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><InformationAssetRegisterPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/impact-assessment" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><ImpactAssessmentPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/impact-assessment/:id" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AssessmentDetailPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/risk-objectives-report" 
        element={
          <ProtectedRouteWithLayout>
            <RiskObjectivesReportPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/business-objectives" 
        element={
          <ProtectedRouteWithLayout>
            <BusinessObjectivesPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/documents" 
        element={
          <ProtectedRouteWithLayout>
            <DocumentLibraryPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/qms" 
        element={
          <ProtectedRouteWithLayout>
            <QMSDashboardPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/questionnaires" 
        element={
          <ProtectedRouteWithLayout>
            <QuestionnairesPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/questionnaires/:questionnaireId" 
        element={
          <ProtectedRouteWithLayout>
            <QuestionnaireDetailPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/trust-portal" 
        element={
          <ProtectedRouteWithLayout>
            <TrustPortalPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/privacy-tools" 
        element={
          <ProtectedRouteWithLayout>
            <PrivacyToolsPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/due-diligence" 
        element={
          <ProtectedRouteWithLayout>
            <DueDiligencePage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/framework-builder" 
        element={
          <ProtectedRouteWithLayout>
            <CustomFrameworkBuilderPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/custom-frameworks" 
        element={
          <ProtectedRouteWithLayout>
            <CustomFrameworksPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRouteWithLayout>
            <AuditReportsPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/analytics" 
        element={
          <ProtectedRouteWithLayout>
            <AdvancedDashboardPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/policy-builder" 
        element={
          <ProtectedRouteWithLayout>
            <PolicyBuilderPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/data-protection" 
        element={
          <ProtectedRouteWithLayout>
            <DataProtectionPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/auditor-management" 
        element={
          <ProtectedRouteWithLayout>
            <AuditorManagementPage />
          </ProtectedRouteWithLayout>
        } 
      />
      {/* Redirect /audit to first sub-nav item */}
      <Route 
        path="/audit" 
        element={
          <ProtectedRouteWithLayout>
            <Navigate to="/trust-portal" replace />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/team" 
        element={
          <ProtectedRouteWithLayout>
            <TeamManagementPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/account" 
        element={
          <ProtectedRouteWithLayout>
            <AccountPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/support-desk" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}>
              <UserSupportDeskPage />
            </Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/calendar" 
        element={
          <ProtectedRouteWithLayout>
            <ComplianceCalendarPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/compliance-calendar" 
        element={
          <ProtectedRouteWithLayout>
            <ComplianceCalendarPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/subprocessors" 
        element={
          <ProtectedRouteWithLayout>
            <SubprocessorRegisterPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/supplier-journey" 
        element={
          <ProtectedRouteWithLayout>
            <SupplierJourneyPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/certification-journey" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}>
              <CertificationJourneyPage />
            </Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/incident-journey" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}>
              <IncidentJourneyPage />
            </Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/board-journey" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}>
              <BoardJourneyPage />
            </Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/vendor-renewal-journey" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}>
              <VendorRenewalJourneyPage />
            </Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ma-target-journey" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}>
              <MATargetJourneyPage />
            </Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/internal-audit" 
        element={
          <ProtectedRouteWithLayout>
            <InternalAuditPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/settings/sso" 
        element={
          <ProtectedRouteWithLayout>
            <SSOSettingsPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/settings/webhooks" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}>
              <WebhookSettingsPage />
            </Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/settings/integrations" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}>
              <IntegrationsSettingsPage />
            </Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/admin/framework-builder" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}>
              <FrameworkBuilderPage />
            </Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRouteWithLayout>
            <AccountPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/settings/account" 
        element={
          <ProtectedRouteWithLayout>
            <AccountPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/settings/team" 
        element={
          <ProtectedRouteWithLayout>
            <TeamManagementPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/settings/billing" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><BillingPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/frameworks" 
        element={
          <ProtectedRouteWithLayout>
            <DashboardPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/board-pack" 
        element={
          <ProtectedRouteWithLayout>
            <BoardPackPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/board-packs-enhanced" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}>
              <BoardPackEnhancedPage />
            </Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/advanced-analytics" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}>
              <AdvancedAnalyticsPage />
            </Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/regulatory-intelligence" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}>
              <RegulatoryIntelligencePage />
            </Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/entities" 
        element={
          <ProtectedRouteWithLayout>
            <MultiEntityPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/training" 
        element={
          <ProtectedRouteWithLayout>
            <EmployeeTrainingPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/training/course/:courseId" 
        element={
          <ProtectedRouteWithLayout>
            <TrainingExecutionPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/training/video-course/:courseId" 
        element={
          <ProtectedRouteWithLayout>
            <VideoCoursePlayerPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/training/analytics" 
        element={
          <ProtectedRouteWithLayout>
            <TrainingAnalyticsPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/training/my-training" 
        element={
          <ProtectedRouteWithLayout>
            <TrainingHistoryPage />
          </ProtectedRouteWithLayout>
        } 
      />
      {/* Incident Management */}
      <Route 
        path="/incidents" 
        element={
          <ProtectedRouteWithLayout>
            <IncidentManagementPage />
          </ProtectedRouteWithLayout>
        } 
      />
      
      {/* AI Governance Routes - Lazy loaded */}
      <Route 
        path="/ai-governance" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AIGovernanceDashboardPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/systems" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AISystemsPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/systems/new" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AISystemsPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/systems/:systemId" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AISystemsPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/risks" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AIRisksPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/risks/new" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AIRisksPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/incidents" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AIIncidentsPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/incidents/new" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AIIncidentsPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/training" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AITrainingPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/training/:moduleId" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AITrainingCoursePage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/policies" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AIPoliciesPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/vendors" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AIVendorsPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/oversight" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AIOversightPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/model-cards" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AIModelCardsPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/bias-audits" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AIBiasAuditsPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/tc-analyzer" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><TCAnalyzerPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/standards-academy" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><StandardsAcademyPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/strategy" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AIStrategyPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-governance/stakeholders" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AIStakeholdersPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/data-export" 
        element={
          <ProtectedRouteWithLayout>
            <DataExportPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/compliance-query" 
        element={
          <ProtectedRouteWithLayout>
            <ComplianceQueryEngine />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/cde-mapping" 
        element={
          <ProtectedRouteWithLayout>
            <CDEMappingPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/vulnerability-scanner" 
        element={
          <ProtectedRouteWithLayout>
            <VulnerabilityScannerPage />
          </ProtectedRouteWithLayout>
        } 
      />
      
      {/* COI Register Routes */}
      <Route 
        path="/cosec/coi-register" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><COIRegisterPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      {/* Public COI Declaration Page (no auth required) */}
      <Route 
        path="/coi/declare/:token" 
        element={
          <Suspense fallback={<PageLoader />}><COIDeclarePage /></Suspense>
        } 
      />
      
      {/* PMO Routes - Pro Tier (Lazy loaded) */}
      <Route 
        path="/pmo" 
        element={
          <GatedRouteWithLayout module="pmo" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><PMODashboardPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/pmo/projects/:projectId" 
        element={
          <GatedRouteWithLayout module="pmo" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><PMOProjectDetailPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/pmo/board-packs" 
        element={
          <GatedRouteWithLayout module="board_packs" requiredTier="Pro">
            <BoardPackPage />
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/pmo/board-packs/:packId" 
        element={
          <GatedRouteWithLayout module="board_packs" requiredTier="Pro">
            <BoardPackPage />
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/pmo/board-management" 
        element={
          <GatedRouteWithLayout module="board_packs" requiredTier="Pro">
            <BoardManagementPage />
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/pmo/analytics" 
        element={
          <GatedRouteWithLayout module="pmo" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><PMOAnalyticsPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      
      {/* Contract Manager Routes - Pro Tier (Lazy loaded) */}
      <Route 
        path="/contracts" 
        element={
          <GatedRouteWithLayout module="contracts" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><ContractsDashboardPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/contracts/vendors/:vendorId" 
        element={
          <GatedRouteWithLayout module="contracts" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><VendorDetailPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/contracts/:contractId" 
        element={
          <GatedRouteWithLayout module="contracts" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><ContractDetailPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      
      {/* Procurement & Tenders Routes - Pro Tier */}
      <Route 
        path="/tenders" 
        element={
          <GatedRouteWithLayout module="contracts" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><TendersDashboardPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/tenders/:tenderId" 
        element={
          <GatedRouteWithLayout module="contracts" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><TenderDetailPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/psl" 
        element={
          <GatedRouteWithLayout module="contracts" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><PSLPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/procurement-analytics" 
        element={
          <GatedRouteWithLayout module="contracts" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><ProcurementAnalyticsPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/vendor-scorecard" 
        element={
          <GatedRouteWithLayout module="contracts" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><VendorScorecardPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/contract-renewals" 
        element={
          <GatedRouteWithLayout module="contracts" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><ContractRenewalsPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/savings-tracker" 
        element={
          <GatedRouteWithLayout module="contracts" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><SavingsTrackerPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      
      {/* M&A Routes - Pro Tier (Lazy loaded) */}
      <Route 
        path="/ma" 
        element={
          <GatedRouteWithLayout module="ma" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><MADashboardPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/ma/reports/comparison" 
        element={
          <GatedRouteWithLayout module="ma" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><MAComparisonReportPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/ma/deals/:dealId" 
        element={
          <GatedRouteWithLayout module="ma" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><DealDetailPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/ma/deals/:dealId/room" 
        element={
          <GatedRouteWithLayout module="deal_room" requiredTier="Pro+">
            <Suspense fallback={<PageLoader />}><DealRoomPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/ma/deals/:dealId/room/:roomId" 
        element={
          <GatedRouteWithLayout module="deal_room" requiredTier="Pro+">
            <Suspense fallback={<PageLoader />}><DealRoomPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/ma/investment-readiness" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><InvestmentReadinessPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ma/onboarding" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><MAOnboardingPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ma/marketplace" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><MAMarketplacePage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      
      {/* Company Secretary Module (Lazy loaded) */}
      <Route 
        path="/cosec" 
        element={
          <GatedRouteWithLayout module="cosec" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><CoSecDashboardPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/cosec/onboarding" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><CoSecOnboardingPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/cosec/entities" 
        element={
          <GatedRouteWithLayout module="cosec" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><EntitiesListPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/cosec/entities/:entityId" 
        element={
          <GatedRouteWithLayout module="cosec" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><EntityGovernancePage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/cosec/structure" 
        element={
          <GatedRouteWithLayout module="cosec" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><CorporateStructurePage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/cosec/tasks" 
        element={
          <GatedRouteWithLayout module="cosec" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><CoSecTasksPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/cosec/tasks/new" 
        element={
          <GatedRouteWithLayout module="cosec" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><CoSecTasksPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/cosec/tasks/:taskId" 
        element={
          <GatedRouteWithLayout module="cosec" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><CoSecTasksPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/cosec/meetings" 
        element={
          <GatedRouteWithLayout module="cosec" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><CoSecMeetingsPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/cosec/meetings/new" 
        element={
          <GatedRouteWithLayout module="cosec" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><CoSecMeetingsPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/cosec/meetings/:meetingId" 
        element={
          <GatedRouteWithLayout module="cosec" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><CoSecMeetingsPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/cosec/calendar" 
        element={
          <GatedRouteWithLayout module="cosec" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><CoSecCalendarPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/cosec/filings" 
        element={
          <GatedRouteWithLayout module="cosec" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><FilingCalendarPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/cosec/filings/new" 
        element={
          <GatedRouteWithLayout module="cosec" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><FilingCalendarPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/cosec/officers" 
        element={
          <GatedRouteWithLayout module="cosec" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><OfficerDirectoryPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/authority" 
        element={
          <GatedRouteWithLayout module="cosec" requiredTier="Pro">
            <Suspense fallback={<PageLoader />}><AuthorityEnginePage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/ma/deals/:dealId/due-diligence" 
        element={
          <GatedRouteWithLayout module="deal_room" requiredTier="Pro+">
            <Suspense fallback={<PageLoader />}><MADueDiligencePage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/ma/deals/:dealId/integration" 
        element={
          <GatedRouteWithLayout module="deal_room" requiredTier="Pro+">
            <Suspense fallback={<PageLoader />}><IntegrationPlaybooksPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/ma/analytics" 
        element={
          <GatedRouteWithLayout module="deal_room" requiredTier="Pro+">
            <Suspense fallback={<PageLoader />}><MAAnalyticsDashboard /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/ma/targets/:targetId" 
        element={
          <ProtectedRouteWithLayout>
            <TargetDetailPage />
          </ProtectedRouteWithLayout>
        } 
      />
      
      {/* M&A Pro+ Middleware & Analyser Routes (Lazy loaded) */}
      <Route 
        path="/ma/deals/:dealId/comparison" 
        element={
          <GatedRouteWithLayout module="ma_plus" requiredTier="Pro+">
            <Suspense fallback={<PageLoader />}><MAPlusComparisonPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/ma/deals/:dealId/employment-analyser" 
        element={
          <GatedRouteWithLayout module="ma_plus" requiredTier="Pro+">
            <Suspense fallback={<PageLoader />}><MAPlusEmploymentAnalyserPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      {/* Alternative route pattern for Employment Analyser */}
      <Route 
        path="/ma-plus/employment-analyser/:dealId" 
        element={
          <GatedRouteWithLayout module="ma_plus" requiredTier="Pro+">
            <Suspense fallback={<PageLoader />}><MAPlusEmploymentAnalyserPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/ma/deals/:dealId/contract-analyser" 
        element={
          <GatedRouteWithLayout module="ma_plus" requiredTier="Pro+">
            <Suspense fallback={<PageLoader />}><MAPlusContractAnalyserPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      
      {/* M&A Pro+ Tool Landing Pages (No deal ID - redirects to deal selection) */}
      <Route 
        path="/ma/pro/:tool" 
        element={
          <GatedRouteWithLayout module="ma_plus" requiredTier="Pro+">
            <Suspense fallback={<PageLoader />}><MAPlusToolLandingPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      
      {/* External Deal Room Access Portal (No Auth Required) */}
      <Route path="/ma/external/room/:accessToken" element={<ExternalAccessPortal />} />
      <Route path="/ma/external/comparison/:accessKey" element={<ExternalComparisonPortal />} />
      
      {/* Standalone Analyser Tools (M&A Pro+ without deal context) */}
      <Route 
        path="/tools/employment-analyser" 
        element={
          <GatedRouteWithLayout module="ma_plus" requiredTier="Pro+">
            <Suspense fallback={<PageLoader />}><StandaloneEmploymentAnalyserPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      <Route 
        path="/tools/contract-analyser" 
        element={
          <GatedRouteWithLayout module="ma_plus" requiredTier="Pro+">
            <Suspense fallback={<PageLoader />}><StandaloneContractAnalyserPage /></Suspense>
          </GatedRouteWithLayout>
        } 
      />
      
      {/* Public DD Supplier Portal */}
      <Route path="/dd-respond/:accessToken" element={<DDSupplierPortalPage />} />
      
      {/* Platform Enhancement Suite */}
      <Route 
        path="/command-center" 
        element={
          <ProtectedRouteWithLayout>
            <CommandCenterPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/rfp-assistant" 
        element={
          <ProtectedRouteWithLayout>
            <RFPAssistantPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/quick-capture" 
        element={
          <ProtectedRouteWithLayout>
            <QuickCapturePage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/notification-hub" 
        element={
          <ProtectedRouteWithLayout>
            <NotificationHubPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/automation-workflows" 
        element={
          <ProtectedRouteWithLayout>
            <AutomationWorkflowsPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/vendor-lifecycle" 
        element={
          <ProtectedRouteWithLayout>
            <VendorLifecyclePage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/vault" 
        element={
          <ProtectedRouteWithLayout>
            <DocumentVaultPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/search" 
        element={
          <ProtectedRouteWithLayout>
            <GlobalSearchPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/activity" 
        element={
          <ProtectedRouteWithLayout>
            <ActivityFeedPage />
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/unified-calendar" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><UnifiedCalendarPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      <Route 
        path="/ai-insights" 
        element={
          <ProtectedRouteWithLayout>
            <Suspense fallback={<PageLoader />}><AIInsightsPage /></Suspense>
          </ProtectedRouteWithLayout>
        } 
      />
      
      {/* Mobile PWA Routes */}
      <Route path="/trust-app/:userId" element={<TrustAppMobile />} />
      <Route path="/trust-app/:userId/:token" element={<TrustAppMobile />} />
      <Route path="/exec-app" element={
        <ProtectedRoute>
          <ExecAppMobile />
        </ProtectedRoute>
      } />
      
      {/* 404 Catch-all Route - must be last */}
      <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFoundPage /></Suspense>} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider>
          <BrowserRouter>
            <AuthProvider>
              <SubscriptionProvider>
                <EntityFilterProvider>
                  <AdminAuthProvider>
                    <OfflineIndicator />
                    <AppRouter />
                    <SupportBot />
                    <CookieConsent />
                    <PWAInstallPrompt />
                    <UpdatePrompt />
                    <Toaster position="top-right" />
                    <ResponsivePreview />
                  </AdminAuthProvider>
                </EntityFilterProvider>
              </SubscriptionProvider>
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;